<template>
    <modal v-model="showModal">
        <template #title>
            <div v-show="!loading">
                <i class="fas fa-building mr-5"></i>
                {{ meeting.title }}
            </div>
        </template>

        <preloader v-if="loading"></preloader>

        <div v-else class="flex flex-col gap-y-7">
            <div v-if="currentUserHasEditAccess" class="flex gap-x-6 items-baseline">
                <ep-button
                    id="edit-team-meeting"
                    start-icon="edit"
                    variant="primary"
                    @click="editTeamMeeting"
                >
                    {{ trans('global.edit') }}
                </ep-button>
                <ep-button
                    id="delete-team-meeting"
                    start-icon="trash"
                    variant="tertiary"
                    @click="deleteTeamMeeting"
                >
                    {{ trans('global.delete') }}
                </ep-button>
            </div>

            <main-section
                :title="trans('online_booking.sidebar.date_time')"
                icon="fa-clock"
            >
                <p class="text-md mb-0 n-color-text-weak">{{ meeting.formatted_date }}</p>
                <p v-show="meeting.recurring_info" class="mb-0 n-color-text-weak">{{ meeting.recurring_info }}</p>
            </main-section>

            <main-section :title="trans('apps.team_meetings.count_participants', { count: participantsCount + 1})" icon="fa-users">
                <div class="flex flex-col gap-6">
                    <participant-details
                        :current-user-has-edit-access="currentUserHasEditAccess"
                        :participant="meeting.organizer"
                        is-organizer
                    />

                    <participant-details
                        v-for="participant in meeting.participants"
                        :key="participant.id"
                        :current-user-has-edit-access="currentUserHasEditAccess"
                        :participant="participant"
                    />
                </div>
            </main-section>

            <main-section
                v-if="meeting.calendar"
                :title="trans('global.calendar')"
                icon="fa-calendar"
            >
                <div class="flex gap-x-4 items-baseline">
                    <i class="fas fa-circle" :style="{color: meeting.calendar.color}"></i>
                    <p class="mb-0">{{ meeting.calendar.name }}</p>
                </div>
            </main-section>

            <main-section
                v-if="meeting.additional_calendars && meeting.additional_calendars.length > 0"
                :title="trans('apps.team_meetings.additional_calendars')"
                icon="fa-calendars"
            >
                <div class="flex flex-col gap-y-4">
                    <div
                        v-for="calendar in meeting.additional_calendars"
                        :key="calendar.id"
                        class="flex gap-x-4 items-baseline"
                    >
                        <i class="fas fa-circle" :style="{color: calendar.color}"></i>
                        <p class="mb-0">{{ calendar.name }}</p>
                    </div>
                </div>
            </main-section>

            <main-section
                v-if="meeting.description"
                :title="trans('client.notes')"
                icon="fa-notes"
            >
                <expandable-text :content="meeting.description"></expandable-text>
            </main-section>

            <main-section
                v-if="currentUserHasEditAccess && calendar_blocking_label"
                :title="trans('apps.team_meetings.calendar_block')"
                icon="fa-calendar-circle-minus"
            >
                <div class="n-color-text-weaker">
                    {{ calendar_blocking_label }} -
                    <a href="" @click.prevent="$event_bus.$emit('showTeamMeetingBlockedCalendarsOverviewModal', meeting)">
                        {{ trans('sendables.view_more') }}
                    </a>
                </div>
            </main-section>
        </div>
    </modal>
</template>

<script>
import { http } from '@/system/http';
import Modal from '@/system/components/global/Modal';
import Preloader from '@/system/components/global/Preloader';
import ExpandableText from '@/system/components/global/ExpandableText';
import MainSection from '@/system/components/apps/team_meetings/MainSection';
import ParticipantDetails from '@/system/components/apps/team_meetings/ParticipantDetails';

export default {
    compatConfig: {
        WATCH_ARRAY: false,
    },
    
    components: {
        ParticipantDetails,
        ExpandableText,
        MainSection,
        Preloader,
        Modal
    },

    data() {
        return {
            loading: false,
            showModal: false,
            meeting: {
                participants: [],
                organizer: {},
            },
            applyRecurringActionTo: null,
        };
    },

    computed: {
        participantsCount() {
            return this.meeting.participants.length || 0;
        },

        user() {
            return this.$store.state.auth.user;
        },

        owner() {
            return this.$store.state.auth.owner;
        },

        isOwner() {
            return this.$store.getters['auth/isOwner'];
        },

        isOrganizer() {
            return this.user.id === this.meeting.organizer?.id;
        },

        calendar_blocking_label () {
            const calendarIds = this.meeting?.calendar_ids_this_team_meeting_is_blocking_collection;

            if(!calendarIds?.length){
                return false;
            }

            const active_blocking_calendar_ids = calendarIds.filter(calendar_blocking => !calendar_blocking.has_exception);

            switch (active_blocking_calendar_ids.length) {
                case 0:
                    return this.trans('calendar_blocking.team_meeting_block_tile_0');
                case 1:
                    return this.trans('calendar_blocking.team_meeting_block_tile_1', {
                        calendar1 : this.$store.getters['calendars/getCalendarById'](calendarIds[0].calendar_id).name
                    });
                case 2:
                    return this.trans('calendar_blocking.team_meeting_block_tile_2',  {
                        calendar1 : this.$store.getters['calendars/getCalendarById'](calendarIds[0].calendar_id).name,
                        calendar2 : this.$store.getters['calendars/getCalendarById'](calendarIds[1].calendar_id).name
                    });
                default:
                    return this.trans('calendar_blocking.team_meeting_block_tile_many', {
                        count : calendarIds.length
                    });
            }
        },

        calendars() {
            return this.$store.getters['calendars/getCalendars']();
        },

        currentUserHasEditAccess () {
            return this.isOwner || this.isOrganizer
        },

        canUserEditCalendar() {
            return this.$store.getters['practitioners/canUserEditCalendar'];
        },

        canUserSeeCalendar() {
            return this.$store.getters['practitioners/canUserSeeCalendar'];
        },
    },

    watch: {
        showModal (newValue) {
            if (!newValue) {
                this.reset();
            }
        },

        'meeting.participants': {
            handler(newValue) {
                if (newValue.length > 0) {
                    for (const participant of newValue) {
                        participant.canSeeMeeting = this.hasMeetingAccess(participant);
                    }
                }
            },
            deep: true,
        },

        'meeting.organizer' (newValue) {
            if (newValue && newValue.id) {
                newValue.canSeeMeeting = true
            }
        },
    },

    mounted() {
        this.showTeamMeetingEventHandler();
        this.deleteRecurrenceEventHandler();
    },

    methods: {
        showTeamMeetingEventHandler() {
            this.$event_bus.$on('showTeamMeetingModal', (meeting, reloadCalendars = false) => {
                this.meeting.id = meeting.id;
                this.showModal = true;
                this.loadTeamMeeting();
                if (reloadCalendars) {
                    this.$event_bus.$emit('calendarReload');
                }
            });
        },

        deleteRecurrenceEventHandler() {
            this.$event_bus.$on('deleteRecurringTeamMeetings', action => {
                this.applyRecurringActionTo = action;
                this.deleteTeamMeetingRequest();
            });
        },

        async loadTeamMeeting() {
            try {
                this.loading = true;
                const { data } = await http.get(`/app/team-meetings/meetings/${this.meeting.id}`)
                this.meeting = data;
            } finally {
                this.loading = false;
            }
        },

        editTeamMeeting() {
            this.$event_bus.$emit('editTeamMeeting', this.meeting);
        },

        deleteTeamMeeting() {
            if (this.meeting.recurring) {
                this.$event_bus.$emit('showMeetingRecurringModal', this.meeting, 'delete');
                return;
            }

            swal({
                title: this.trans('apps.team_meetings.delete'),
                text: this.trans('apps.team_meetings.delete_confirm'),
                showCancelButton: true,
                html: true,
                cancelButtonText: this.trans('global.cancel'),
                showLoaderOnConfirm: true,
                closeOnConfirm: true,
            }, async () => {
                await this.deleteTeamMeetingRequest();
            });
        },

        async deleteTeamMeetingRequest() {
            this.loading = true;
            try {
                const config = this.applyRecurringActionTo
                    ? { params: { action: this.applyRecurringActionTo } }
                    : null;

                const { data } = await http.delete(`/app/team-meetings/meetings/${this.meeting.id}`, config);

                toastr.success(data.success);
                this.showModal = false;
                this.$event_bus.$emit('recurringTeamMeetingAdjusted');
            } finally {
                this.loading = false;
            }
        },

        reset() {
            this.meeting = {
                participants: [],
                organizer: {},
            };
            this.applyRecurringActionTo = null;
            this.loading = false;
        },

        hasMeetingAccess (participant) {
            // Check if the participant is the owner or organizer
            if (participant.id === this.owner?.id || participant.id === this.meeting?.organizer?.id) return true;

            const organizerId = this.meeting.organizer?.id || this.user.id;
            // Check if the participant has access to any of the additional calendars that belong to the participant
            const participantMeetingCalendars = this.calendars
                .filter(calendar => calendar.practitioner_user?.id === participant.id)
                .filter(calendar => this.meeting.additional_calendars?.some(cal => cal.id === calendar.id));

            // check which one of the above calendars the participant has access to, and then check if the organizer has edit access to it
            const sharedCalendars = participantMeetingCalendars.filter(calendar => {
                const participantCanSee = this.canUserSeeCalendar(participant, calendar.id);
                const organizerCanEdit = this.canUserEditCalendar(organizerId, calendar.id);
                return participantCanSee && (organizerCanEdit || this.isOwner);
            });

            // If any calendar exists, grant access
            if (sharedCalendars.length > 0) return true;

            // Check if the participant has access to the meeting's calendar
            const participantHasAccessToMeetingCalendar = this.canUserSeeCalendar(participant, this.meeting.calendar_id);
            if (participantHasAccessToMeetingCalendar) return true;

            // Default to no access
            return false;
        },
    },
};
</script>
